import React, { useState, useEffect } from "react";
import { getOverpassWorkerService } from "../../services/overpassWorkerService";
import debugx from "debugx";

const debug = debugx("bikedash:settings");

export const CacheManagementSection: React.FC = () => {
  const [cacheStats, setCacheStats] = useState<{
    count: number;
    oldestDate: Date | null;
    newestDate: Date | null;
    gridSize?: number;
    paddingMiles?: number;
    totalSize?: number;
  }>({ count: 0, oldestDate: null, newestDate: null });
  const [isClearing, setIsClearing] = useState(false);
  const [apiState, setApiState] = useState<
    | {
        suspended: boolean;
        suspendedAt: number | null;
        suspendReason: string | null;
        manualSuspension: boolean;
        consecutive429Count: number;
        total429Count: number;
        total504Count: number;
        totalRequests: number;
        totalErrors: number;
        lastRequestTime: number;
        nextRequestAvailableAt: number;
        currentlyWaiting: boolean;
        waitingUntil: number | null;
        waitReason: string | null;
      }
    | null
    | "loading"
  >("loading");
  const [waitTimeLeft, setWaitTimeLeft] = useState<number>(0);
  const [apiConfig, setApiConfig] = useState({
    requestInterval: 2,
    firstRetryWait: 10,
    secondRetryWait: 30,
    maxRetries: 3,
    apiEndpoint: "",
  });
  const [uiConfig, setUiConfig] = useState({
    cacheGridSize: 3,
    alwaysShowCacheGrid: false,
  });

  const loadApiConfig = () => {
    const stored = localStorage.getItem("overpassApiConfig");
    if (stored) {
      try {
        const config = JSON.parse(stored);
        if (config.apiEndpoint === undefined) {
          config.apiEndpoint = "";
        }
        setApiConfig(config);
        applyApiConfig(config);
      } catch (error) {
        debug.warn("Failed to load API config:", error);
      }
    }
  };

  const loadUiConfig = () => {
    const stored = localStorage.getItem("cacheGridUiConfig");
    if (stored) {
      try {
        const config = JSON.parse(stored);
        setUiConfig({ ...uiConfig, ...config });
      } catch (error) {
        debug.warn("Failed to load UI config:", error);
      }
    }
  };

  const saveUiConfig = (config: typeof uiConfig) => {
    localStorage.setItem("cacheGridUiConfig", JSON.stringify(config));
    setUiConfig(config);
    window.dispatchEvent(
      new CustomEvent("cacheGridUiConfigChanged", { detail: config }),
    );
  };

  const saveApiConfig = (config: typeof apiConfig) => {
    localStorage.setItem("overpassApiConfig", JSON.stringify(config));
    setApiConfig(config);
    applyApiConfig(config);
  };

  const applyApiConfig = (config: typeof apiConfig) => {
    const workerService = getOverpassWorkerService();
    workerService.updateApiConfig(config).catch((error: any) => {
      debug.warn("Failed to update worker API config:", error);
    });
    debug(
      "API config updated in worker, saved for main thread on refresh:",
      config,
    );
  };

  const loadApiState = async () => {
    try {
      const workerService = getOverpassWorkerService();
      const state = await workerService.getApiState();
      setApiState(state);
    } catch (error) {
      debug.warn("Failed to get API state:", error);
      getOverpassWorkerService()
        .getApiState()
        .then((state) => {
          setApiState(state);
        });
    }
  };

  useEffect(() => {
    const workerService = getOverpassWorkerService();
    workerService
      .getCacheStats()
      .then((stats) => {
        setCacheStats(stats);
      })
      .catch((error) => {
        debug.warn(
          "Worker failed to get cache stats, using main thread:",
          error,
        );
        getOverpassWorkerService()
          .getCacheStats()
          .then((stats) => {
            setCacheStats(stats);
          });
      });

    loadApiState();
    loadApiConfig();
    loadUiConfig();

    const handleApiStateChange = (e: CustomEvent) => {
      setApiState(e.detail);
    };
    window.addEventListener(
      "overpassApiStateChanged",
      handleApiStateChange as any,
    );

    const interval = setInterval(() => {
      if (
        apiState &&
        apiState !== "loading" &&
        apiState.currentlyWaiting &&
        apiState.waitingUntil
      ) {
        const timeLeft = Math.max(
          0,
          Math.ceil((apiState.waitingUntil - Date.now()) / 1000),
        );
        setWaitTimeLeft(timeLeft);
      } else {
        setWaitTimeLeft(0);
      }
    }, 1000);

    return () => {
      window.removeEventListener(
        "overpassApiStateChanged",
        handleApiStateChange as any,
      );
      clearInterval(interval);
    };
  }, [
    apiState && apiState !== "loading" ? apiState.currentlyWaiting : false,
    apiState && apiState !== "loading" ? apiState.waitingUntil : null,
  ]);

  const handleResetApiSuspension = async () => {
    try {
      const workerService = getOverpassWorkerService();
      await workerService.resetApiSuspension();
      getOverpassWorkerService().resetApiSuspension();
      debug("API suspension reset");
      loadApiState();
    } catch (error) {
      debug.error("Failed to reset API suspension:", error);
    }
  };

  const handleToggleManualSuspension = async () => {
    try {
      const workerService = getOverpassWorkerService();
      const newState = await workerService.toggleManualSuspension();
      setApiState(newState);
      debug("Manual suspension toggled");
    } catch (error) {
      debug.error("Failed to toggle manual suspension:", error);
    }
  };

  const handleClearCache = async () => {
    if (isClearing) return;

    const confirmClear = window.confirm(
      `Are you sure you want to clear ${cacheStats.count} cached grid square${cacheStats.count !== 1 ? "s" : ""}?`,
    );

    if (!confirmClear) return;

    setIsClearing(true);
    try {
      const workerService = getOverpassWorkerService();
      let workerSuccess = false;
      let mainSuccess = false;

      try {
        workerSuccess = await workerService.clearCache();
        debug("Worker cache cleared: %s", workerSuccess);
      } catch (error) {
        debug.warn("Worker failed to clear cache:", error);
      }

      try {
        mainSuccess = await getOverpassWorkerService().clearCache();
        debug("Main thread cache cleared: %s", mainSuccess);
      } catch (error) {
        debug.warn("Main thread failed to clear cache:", error);
      }

      if (workerSuccess || mainSuccess) {
        debug("Cache cleared successfully");
        setCacheStats({
          count: 0,
          oldestDate: null,
          newestDate: null,
          totalSize: 0,
        });
      } else {
        alert("Failed to clear cache. Please try again.");
      }
    } catch (error) {
      debug.error("Error clearing cache:", error);
      alert("Error clearing cache. Please check console for details.");
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="setting-group">
      <label>Overpass API & Cache</label>
      <p className="setting-description">
        Cached local search results and API configuration.
      </p>

      {/* Cache Stats Section */}
      <div
        style={{
          marginTop: "12px",
          padding: "12px",
          background: "var(--bg-subtle)",
          borderRadius: "4px",
        }}
      >
        <div style={{ marginBottom: "12px" }}>
          <div style={{ fontSize: "14px", marginBottom: "4px" }}>
            <strong>Grid squares cached:</strong> {cacheStats.count}
          </div>
          {cacheStats.totalSize !== undefined && cacheStats.totalSize > 0 && (
            <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
              <strong>Total size:</strong>{" "}
              {cacheStats.totalSize < 1024
                ? `${cacheStats.totalSize} bytes`
                : cacheStats.totalSize < 1024 * 1024
                  ? `${(cacheStats.totalSize / 1024).toFixed(1)} KB`
                  : `${(cacheStats.totalSize / (1024 * 1024)).toFixed(2)} MB`}
            </div>
          )}
          {cacheStats.gridSize !== undefined && (
            <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
              <strong>Grid size:</strong> {cacheStats.gridSize}°{" | "}
              <strong>Padding:</strong> {cacheStats.paddingMiles} mile
            </div>
          )}
          {cacheStats.oldestDate && (
            <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
              <strong>Oldest:</strong>{" "}
              {cacheStats.oldestDate.toLocaleDateString()}
            </div>
          )}
          {cacheStats.newestDate && (
            <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
              <strong>Newest:</strong>{" "}
              {cacheStats.newestDate.toLocaleDateString()}
            </div>
          )}
        </div>
        <button
          className="control-btn"
          onClick={handleClearCache}
          disabled={isClearing || cacheStats.count === 0}
          style={{
            width: "100%",
            opacity: cacheStats.count === 0 ? 0.5 : 1,
            border: "1px solid var(--border)",
            background: "var(--bg-subtle)",
          }}
        >
          {isClearing
            ? "Clearing..."
            : `Clear ${cacheStats.count} Grid Cache${cacheStats.count !== 1 ? "s" : ""}`}
        </button>
      </div>

      {/* API Status Section */}
      {(apiState === "loading" || apiState) && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            background:
              apiState === "loading"
                ? "var(--bg-subtle)"
                : apiState && apiState.suspended
                  ? "rgba(255, 0, 0, 0.1)"
                  : apiState && apiState.currentlyWaiting
                    ? "rgba(255, 165, 0, 0.1)"
                    : "var(--bg-subtle)",
            borderRadius: "4px",
            border:
              apiState && apiState !== "loading" && apiState.suspended
                ? "1px solid var(--danger)"
                : "1px solid var(--border)",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            API Status
          </div>

          {apiState === "loading" ? (
            <div
              style={{ color: "var(--text-secondary)", marginBottom: "8px" }}
            >
              <strong>⏳ Checking...</strong>
            </div>
          ) : apiState && apiState.suspended ? (
            <div style={{ color: "#ff6b6b", marginBottom: "12px" }}>
              <strong>⚠️ API SUSPENDED</strong>
              <div style={{ fontSize: "12px", marginTop: "4px" }}>
                {apiState.suspendReason}
                {apiState.suspendedAt && (
                  <div
                    style={{ fontSize: "11px", color: "var(--text-secondary)" }}
                  >
                    Suspended{" "}
                    {Math.floor((Date.now() - apiState.suspendedAt) / 60000)}{" "}
                    minutes ago
                  </div>
                )}
              </div>
            </div>
          ) : apiState && apiState.currentlyWaiting ? (
            <div style={{ color: "#ffd93d", marginBottom: "12px" }}>
              <strong>⏳ WAITING</strong>
              <div style={{ fontSize: "12px", marginTop: "4px" }}>
                {apiState.waitReason} - {waitTimeLeft}s remaining
              </div>
            </div>
          ) : (
            <div style={{ color: "#6bcf7f", marginBottom: "8px" }}>
              <strong>✓ Ready</strong>
            </div>
          )}

          {apiState && apiState !== "loading" && (
            <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
              <div>
                <strong>Requests:</strong> {apiState.totalRequests}
              </div>
              {apiState.totalErrors > 0 && (
                <div>
                  <strong>Errors:</strong> {apiState.totalErrors}
                </div>
              )}
              {apiState.total429Count > 0 && (
                <div
                  style={{
                    color:
                      apiState.consecutive429Count > 0 ? "#ff6b6b" : "inherit",
                  }}
                >
                  <strong>Rate Limited:</strong> {apiState.total429Count}
                  {apiState.consecutive429Count > 0 &&
                    ` (${apiState.consecutive429Count} consecutive)`}
                </div>
              )}
              {apiState.total504Count > 0 && (
                <div>
                  <strong>Timeouts:</strong> {apiState.total504Count}
                </div>
              )}
            </div>
          )}

          {apiState && apiState !== "loading" && (
            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
              {(!apiState.suspended || apiState.manualSuspension) && (
                <button
                  className="control-btn"
                  onClick={handleToggleManualSuspension}
                  style={{
                    flex: 1,
                    backgroundColor:
                      apiState.suspended && apiState.manualSuspension
                        ? "rgba(107, 255, 107, 0.2)"
                        : "rgba(255, 165, 0, 0.2)",
                    border:
                      apiState.suspended && apiState.manualSuspension
                        ? "1px solid var(--success)"
                        : "1px solid var(--warning)",
                    color:
                      apiState.suspended && apiState.manualSuspension
                        ? "var(--success)"
                        : "var(--warning)",
                  }}
                >
                  {apiState.suspended && apiState.manualSuspension
                    ? "Resume API Calls"
                    : "Suspend API Calls"}
                </button>
              )}

              {apiState.suspended && !apiState.manualSuspension && (
                <button
                  className="control-btn"
                  onClick={handleResetApiSuspension}
                  style={{
                    flex: 1,
                    backgroundColor: "rgba(255, 107, 107, 0.2)",
                    border: "1px solid var(--danger)",
                    color: "var(--danger)",
                  }}
                >
                  Reset API Suspension
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* API Configuration Section */}
      <div className="slider-setting" style={{ marginTop: "16px" }}>
        <label>Request Interval: {apiConfig.requestInterval}s</label>
        <input
          type="range"
          min="1"
          max="10"
          step="1"
          value={apiConfig.requestInterval}
          onChange={(e) => {
            const newConfig = {
              ...apiConfig,
              requestInterval: Number(e.target.value),
            };
            saveApiConfig(newConfig);
          }}
        />
      </div>

      <div className="slider-setting">
        <label>First Retry Wait: {apiConfig.firstRetryWait}s</label>
        <input
          type="range"
          min="1"
          max="120"
          step="1"
          value={apiConfig.firstRetryWait}
          onChange={(e) => {
            const newConfig = {
              ...apiConfig,
              firstRetryWait: Number(e.target.value),
            };
            saveApiConfig(newConfig);
          }}
        />
      </div>

      <div className="slider-setting">
        <label>Second Retry Wait: {apiConfig.secondRetryWait}s</label>
        <input
          type="range"
          min="5"
          max="120"
          step="5"
          value={apiConfig.secondRetryWait}
          onChange={(e) => {
            const newConfig = {
              ...apiConfig,
              secondRetryWait: Number(e.target.value),
            };
            saveApiConfig(newConfig);
          }}
        />
      </div>

      <div className="slider-setting">
        <label>Max Retries: {apiConfig.maxRetries}</label>
        <input
          type="range"
          min="1"
          max="5"
          step="1"
          value={apiConfig.maxRetries}
          onChange={(e) => {
            const newConfig = {
              ...apiConfig,
              maxRetries: Number(e.target.value),
            };
            saveApiConfig(newConfig);
          }}
        />
      </div>

      {/* Optional API endpoint override */}
      <details style={{ marginTop: "16px" }}>
        <summary
          style={{
            cursor: "pointer",
            color: "var(--text-secondary)",
            fontSize: "12px",
          }}
        >
          Advanced: Custom API endpoint
        </summary>
        <div style={{ marginTop: "12px" }}>
          <p className="setting-description" style={{ fontSize: "11px" }}>
            Override the default Overpass API endpoint. Leave empty to use
            default (overpass-api.de).
          </p>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="text"
              placeholder="https://bikedash-osm.bitbased.net/api/interpreter"
              value={apiConfig.apiEndpoint}
              onChange={(e) => {
                const newConfig = { ...apiConfig, apiEndpoint: e.target.value };
                setApiConfig(newConfig);
                saveApiConfig(newConfig);
              }}
              style={{
                flex: 1,
                padding: "6px",
                fontSize: "11px",
                fontFamily: "monospace",
                background: "var(--bg-subtle)",
                border: "1px solid var(--border)",
                borderRadius: "4px",
                color: "var(--text-primary)",
              }}
            />
            <button
              className="control-btn"
              onClick={() => {
                const newConfig = { ...apiConfig, apiEndpoint: "" };
                saveApiConfig(newConfig);
              }}
              style={{
                padding: "6px 12px",
                fontSize: "11px",
                border: "1px solid var(--border)",
                background: "var(--bg-subtle)",
              }}
            >
              {apiConfig.apiEndpoint ? "Clear" : "Default"}
            </button>
          </div>
          <div
            style={{
              fontSize: "10px",
              color: "var(--text-secondary)",
              marginTop: "8px",
            }}
          >
            Alternative servers (click to use):
            <br />
            <span
              style={{
                cursor: "pointer",
                color: "var(--link-color, #4a9eff)",
                textDecoration: "underline",
              }}
              onClick={() => {
                if (!apiConfig.apiEndpoint.trim()) {
                  const newConfig = {
                    ...apiConfig,
                    apiEndpoint: "https://overpass-api.de/api/interpreter",
                  };
                  setApiConfig(newConfig);
                  saveApiConfig(newConfig);
                }
              }}
              title="Click to use this server"
            >
              • overpass-api.de/api/interpreter
            </span>
            <br />
            <span
              style={{
                cursor: "pointer",
                color: "var(--link-color, #4a9eff)",
                textDecoration: "underline",
              }}
              onClick={() => {
                if (!apiConfig.apiEndpoint.trim()) {
                  const newConfig = {
                    ...apiConfig,
                    apiEndpoint:
                      "https://overpass.kumi.systems/api/interpreter",
                  };
                  setApiConfig(newConfig);
                  saveApiConfig(newConfig);
                }
              }}
              title="Click to use this server"
            >
              • overpass.kumi.systems/api/interpreter
            </span>
          </div>
        </div>
      </details>

      {/* UI Configuration Section */}
      <div
        style={{
          marginTop: "24px",
          paddingTop: "16px",
          borderTop: "1px solid var(--border)",
        }}
      >
        <h3
          style={{
            fontSize: "14px",
            marginBottom: "12px",
            color: "var(--text-primary)",
          }}
        >
          Cache Grid Display
        </h3>

        <div className="slider-setting">
          <div style={{ display: "flex", gap: "8px" }}>
            {[3, 5, 7].map((size) => (
              <button
                key={size}
                className="control-btn"
                onClick={() =>
                  saveUiConfig({ ...uiConfig, cacheGridSize: size })
                }
                style={{
                  flex: 1,
                  padding: "8px",
                  background:
                    uiConfig.cacheGridSize === size
                      ? "var(--accent)"
                      : "var(--bg-subtle)",
                  border:
                    uiConfig.cacheGridSize === size
                      ? "2px solid var(--accent)"
                      : "1px solid var(--border)",
                  opacity: uiConfig.cacheGridSize === size ? 1 : 0.6,
                }}
              >
                {size}×{size}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: "12px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={uiConfig.alwaysShowCacheGrid}
              onChange={(e) =>
                saveUiConfig({
                  ...uiConfig,
                  alwaysShowCacheGrid: e.target.checked,
                })
              }
              style={{ cursor: "pointer" }}
            />
            <span style={{ fontSize: "12px", color: "var(--text-primary)" }}>
              Always show cache grid instead of search icon
            </span>
          </label>
          <p
            className="setting-description"
            style={{ fontSize: "11px", marginTop: "4px", marginLeft: "24px" }}
          >
            When enabled, the cache grid will be visible even without an active
            search
          </p>
        </div>
      </div>

      <button
        className="control-btn"
        onClick={() => {
          const defaults = {
            requestInterval: 2,
            firstRetryWait: 10,
            secondRetryWait: 30,
            maxRetries: 3,
            apiEndpoint: "",
          };
          saveApiConfig(defaults);
        }}
        style={{
          marginTop: "12px",
          width: "100%",
          opacity: 0.8,
          border: "1px solid var(--border)",
          background: "var(--bg-subtle)",
        }}
      >
        Reset to Defaults
      </button>
    </div>
  );
};
